import { lazy } from 'react';
import LazyRouteRecoveryFallback from '@/components/routing/LazyRouteRecoveryFallback.jsx';

const STORAGE_PREFIX = 'gsync:lazy-route-recovery:';
const RECOVERABLE_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Failed to load module script/i,
  /error loading dynamically imported module/i,
];

export function getLazyRouteRecoveryStorageKey(recoveryKey) {
  return `${STORAGE_PREFIX}${recoveryKey}`;
}

export function isRecoverableLazyRouteError(error) {
  const message = String(error?.message || error || '').trim();
  if (!message) {
    return false;
  }
  return RECOVERABLE_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export function hasLazyRouteRecoveryAttempt(recoveryKey) {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(getLazyRouteRecoveryStorageKey(recoveryKey)) === '1';
  } catch {
    return false;
  }
}

export function markLazyRouteRecoveryAttempt(recoveryKey) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(getLazyRouteRecoveryStorageKey(recoveryKey), '1');
  } catch {
    // Ignore storage failures and fall back to a simple reload attempt.
  }
}

export function clearLazyRouteRecoveryAttempt(recoveryKey) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(getLazyRouteRecoveryStorageKey(recoveryKey));
  } catch {
    // Ignore storage failures.
  }
}

export function lazyRouteWithRecovery(importer, { recoveryKey }) {
  if (!recoveryKey) {
    throw new Error('lazyRouteWithRecovery requires a recoveryKey');
  }

  return lazy(async () => {
    try {
      const module = await importer();
      clearLazyRouteRecoveryAttempt(recoveryKey);
      return module;
    } catch (error) {
      const shouldAutoReload =
        isRecoverableLazyRouteError(error) && !hasLazyRouteRecoveryAttempt(recoveryKey);

      if (shouldAutoReload && typeof window !== 'undefined') {
        markLazyRouteRecoveryAttempt(recoveryKey);
        console.warn(`[lazyRouteWithRecovery] Reloading after lazy route failure: ${recoveryKey}`, error);
        window.location.reload();
        return new Promise(() => {});
      }

      console.error(`[lazyRouteWithRecovery] Unable to load route: ${recoveryKey}`, error);
      return {
        default: () => (
          <LazyRouteRecoveryFallback
            recoveryKey={recoveryKey}
          />
        ),
      };
    }
  });
}
